import { STREAM_ACTIONS } from '../actions/StreamActions';

const initialState = {
  streams: [],
  selectedStream: null,
  stats: {
    total_streams: 0,
    active_streams: 0,
    inactive_streams: 0,
    disconnected_streams: 0,
    total_viewers: 0,
    total_bandwidth: 0,
    nginx_stats: {
      total_streams: 0,
      total_clients: 0,
      total_bandwidth_in: 0,
      total_bandwidth_out: 0
    }
  },
  loading: false,
  error: null,
  creating: false,
  updating: false,
  deleting: false,
  regenerating: false
};

const StreamReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch all streams
    case STREAM_ACTIONS.FETCH_STREAMS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case STREAM_ACTIONS.FETCH_STREAMS_SUCCESS:
      return {
        ...state,
        loading: false,
        streams: action.payload,
        error: null
      };
    
    case STREAM_ACTIONS.FETCH_STREAMS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    // Fetch single stream
    case STREAM_ACTIONS.FETCH_STREAM_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case STREAM_ACTIONS.FETCH_STREAM_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedStream: action.payload,
        error: null
      };
    
    case STREAM_ACTIONS.FETCH_STREAM_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    // Create stream
    case STREAM_ACTIONS.CREATE_STREAM_REQUEST:
      return {
        ...state,
        creating: true,
        error: null
      };
    
    case STREAM_ACTIONS.CREATE_STREAM_SUCCESS:
      return {
        ...state,
        creating: false,
        streams: [action.payload, ...state.streams],
        error: null
      };
    
    case STREAM_ACTIONS.CREATE_STREAM_FAILURE:
      return {
        ...state,
        creating: false,
        error: action.payload
      };
    
    // Update stream
    case STREAM_ACTIONS.UPDATE_STREAM_REQUEST:
      return {
        ...state,
        updating: true,
        error: null
      };
    
    case STREAM_ACTIONS.UPDATE_STREAM_SUCCESS:
      return {
        ...state,
        updating: false,
        streams: state.streams.map(stream => 
          stream.id === action.payload.id 
            ? { ...stream, ...action.payload.updates }
            : stream
        ),
        selectedStream: state.selectedStream && state.selectedStream.id === action.payload.id
          ? { ...state.selectedStream, ...action.payload.updates }
          : state.selectedStream,
        error: null
      };
    
    case STREAM_ACTIONS.UPDATE_STREAM_FAILURE:
      return {
        ...state,
        updating: false,
        error: action.payload
      };
    
    // Delete stream
    case STREAM_ACTIONS.DELETE_STREAM_REQUEST:
      return {
        ...state,
        deleting: true,
        error: null
      };
    
    case STREAM_ACTIONS.DELETE_STREAM_SUCCESS:
      return {
        ...state,
        deleting: false,
        streams: state.streams.filter(stream => stream.id !== action.payload),
        selectedStream: state.selectedStream && state.selectedStream.id === action.payload
          ? null
          : state.selectedStream,
        error: null
      };
    
    case STREAM_ACTIONS.DELETE_STREAM_FAILURE:
      return {
        ...state,
        deleting: false,
        error: action.payload
      };
    
    // Fetch stats
    case STREAM_ACTIONS.FETCH_STATS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case STREAM_ACTIONS.FETCH_STATS_SUCCESS:
      return {
        ...state,
        loading: false,
        stats: action.payload,
        error: null
      };
    
    case STREAM_ACTIONS.FETCH_STATS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    // Real-time status update
    case STREAM_ACTIONS.UPDATE_STREAM_STATUS: {
      const metadata = action.payload.metadata || {};
      const normalizedViewerCount =
        metadata.viewer_count ??
        metadata.live_count ??
        metadata.clients;
      const normalizedIsLive =
        metadata.is_live ??
        metadata.isLive ??
        (typeof normalizedViewerCount === 'number' ? normalizedViewerCount > 0 : undefined);
      const normalizedStatus =
        action.payload.status ||
        metadata.status ||
        (normalizedIsLive ? 'broadcasting' : undefined);

      const normalizedUpdates = {
        ...metadata,
        ...(normalizedStatus ? { status: normalizedStatus } : {}),
        ...(normalizedIsLive !== undefined ? { is_live: Boolean(normalizedIsLive) } : {}),
        ...(normalizedViewerCount !== undefined ? { viewer_count: normalizedViewerCount } : {})
      };

      return {
        ...state,
        streams: state.streams.map(stream => 
          stream.stream_key === action.payload.streamKey
            ? { 
                ...stream, 
                ...normalizedUpdates
              }
            : stream
        ),
        selectedStream: state.selectedStream && state.selectedStream.stream_key === action.payload.streamKey
          ? { 
              ...state.selectedStream, 
              ...normalizedUpdates
            }
          : state.selectedStream
      };
    }
    
    // Set selected stream
    case STREAM_ACTIONS.SET_SELECTED_STREAM:
      return {
        ...state,
        selectedStream: action.payload
      };
    
    // Clear error
    case STREAM_ACTIONS.CLEAR_STREAM_ERROR:
      return {
        ...state,
        error: null
      };
    
    // Regenerate stream key
    case STREAM_ACTIONS.REGENERATE_KEY_REQUEST:
      return {
        ...state,
        regenerating: true,
        error: null
      };
    
    case STREAM_ACTIONS.REGENERATE_KEY_SUCCESS:
      return {
        ...state,
        regenerating: false,
        streams: state.streams.map(stream => 
          stream.id === action.payload.id 
            ? { ...stream, stream_key: action.payload.stream_key }
            : stream
        ),
        selectedStream: state.selectedStream && state.selectedStream.id === action.payload.id
          ? { ...state.selectedStream, stream_key: action.payload.stream_key }
          : state.selectedStream,
        error: null
      };
    
    case STREAM_ACTIONS.REGENERATE_KEY_FAILURE:
      return {
        ...state,
        regenerating: false,
        error: action.payload
      };
    
    default:
      return state;
  }
};

export default StreamReducer;
